import type { AuthResult, BranchChoice, BusinessMemberRole, Permission, PublicSettings, RateView, UserSummary } from '@pidelo/contracts';
import type { BaseSession } from '../session/session-store.ts';

export type AdminAuthResult = Extract<AuthResult, { audience: 'ADMIN' }>;
export type BusinessAuthResult = Extract<AuthResult, { audience: 'BUSINESS' }>;

/*
 * The answers below have no schema in contracts yet; these types follow what the API returns today.
 * When contracts adds them, these become aliases of the inferred types.
 */

/** GET /v1/admin/session. */
export type AdminSessionView = {
  user: UserSummary;
  isOwner: boolean;
  permissions: Permission[];
  shift: { id: string; startedAt: string } | null;
};

/** GET /v1/business/session. */
export type BusinessSessionView = {
  /** Read again from the server on each request: a new role applies at once (T5.3). */
  role?: BusinessMemberRole;
  user: UserSummary;
  business: { id: string; name: string };
  branches: BranchChoice[];
  branchId: string | null;
};

/** GET /v1/public/config. */
export type PublicConfig = {
  settings: PublicSettings;
  rate: Pick<RateView, 'rate' | 'effectiveDate' | 'source'>;
};

/** What the admin keeps in its session store (the token plus what the menu needs before the first request). */
export type AdminSession = BaseSession & { audience: 'ADMIN'; user: UserSummary; isOwner: boolean; permissions: Permission[] };

/** What the business page keeps: the branch chosen decides which orders arrive. */
export type BusinessSession = BaseSession & {
  audience: 'BUSINESS';
  /** Dueño, encargado, mesonero, cocina o caja: what the page shows; the server decides what it allows. */
  role?: BusinessMemberRole;
  user: UserSummary;
  business: { id: string; name: string };
  branches: BranchChoice[];
  branchId: string | null;
};

export function adminSessionFrom(result: AdminAuthResult): AdminSession {
  return { audience: 'ADMIN', token: result.token, user: result.user, isOwner: result.isOwner, permissions: result.permissions };
}

export function businessSessionFrom(result: BusinessAuthResult): BusinessSession {
  return { audience: 'BUSINESS', token: result.token, ...(result.role ? { role: result.role } : {}), user: result.user, business: result.business, branches: result.branches, branchId: result.branchId };
}
